import React from 'react';
import { useRecoilValue } from 'recoil';
import SmallText from 'src/components/SmallText';
import { useOpenPopup } from '../../hooks/useOpenPopup';
import { abilityItemSelector, imageItemSelector } from '../../store/selector';
import { imagePopupState } from '../../store/state';
import ImageButton from '../ImageButton';
import ImageOperationPopup from '../ImageOpreationPopup';
import ImageStars from '../ImageStars';
import styles from './index.module.scss';
import DefaultAbilityLogo from '../../images/default-ability@3x.png';

interface ItemProps {
  title: string;
  logo: string;
  content: string;
  buttonText: string;
  onClick?: () => any;
}
const Item: React.FC<ItemProps> = ({
  title,
  logo,
  content,
  onClick,
  buttonText
}) => (
  <div className={styles.ability}>
    <div className={styles.name}>
      <span>{title}：</span>
    </div>
    <div className={styles.logo} style={{ backgroundImage: `url(${logo})` }} />
    <SmallText className={styles.desc} fontSize={22}>
      {content}
    </SmallText>
    {onClick && <button onClick={onClick}>{buttonText}</button>}
  </div>
);

interface AbilityItemProps {
  id?: number;
  onClick?: () => any;
  isHigh?: boolean;
}

const AbilityItem = React.memo(({ isHigh, id, onClick }: AbilityItemProps) => {
  const item = useRecoilValue(abilityItemSelector(id || 0));
  return (
    <Item
      title={isHigh ? '高级天赋' : '普通天赋'}
      buttonText={isHigh ? '重置高级' : '重置天赋'}
      logo={item?.logo || DefaultAbilityLogo}
      content={item ? `${item.title}：${item.desc}` : '未激活'}
      onClick={onClick}
    />
  );
});

const AbilitiesPopup = () => {
  const popup = useRecoilValue(imagePopupState('abilities'));
  const item = useRecoilValue(imageItemSelector(popup.target));
  const open = useOpenPopup();
  if (!item) return null;
  const upgrade = () => open('upgrade', item.id);
  return (
    <ImageOperationPopup
      type="abilities"
      className={styles.container}
      title="查看天赋"
      position="bottom"
      confirmText="升星"
      customSubButton={
        <ImageButton
          type="primary"
          size="large"
          onClick={() => open('reset', item.id, 3)}
        >
          重置属性
        </ImageButton>
      }
      onClick={item.canUpStar && upgrade}
    >
      <div className={styles.info}>
        <img className={styles.logo} src={item.url} alt="" />
        <div className={styles.props}>
          <div className={styles.name}>{item.title}</div>
          <ImageStars
            count={item.upStar?.level || 0}
            className={styles.stars}
          />
          <div className={styles.prop}>
            <span>攻击力：{item.attackValue}</span>
          </div>
          <div className={styles.prop}>
            <span>生命值：{item.healthPoint}</span>
          </div>
          <div className={styles.score}>
            <span>战力 {item.fightingValue}</span>
          </div>
        </div>
      </div>
      <div
        className={styles.content}
        style={item.userImageTalentList?.length === 0 ? { padding: 0 } : {}}
      >
        <AbilityItem
          id={item.talent}
          onClick={
            item.level >= 3 ? () => open('reset', item.id, 1) : undefined
          }
        />
        <AbilityItem
          isHigh
          id={item.highTalent}
          onClick={
            item.level === 4 ? () => open('reset', item.id, 2) : undefined
          }
        />
        {item.deBuff && item.deBuff.level > 0 && (
          <Item
            title="状态"
            logo={item.deBuff.logo}
            content={`${item.deBuff.title}：${item.deBuff.desc}`}
            onClick={() => open('reset', item.id, 4)}
            buttonText="解除异常"
          />
        )}
      </div>
    </ImageOperationPopup>
  );
};

export default React.memo(AbilitiesPopup);
